import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { Layers, Plus, Search, Edit, Trash2, Save, Loader2, ArrowLeft, RotateCcw } from 'lucide-react';
import toast from '../utils/toast';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterToolbar,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterTitle,
  masterTableData
} from '../components/masterPageStyles';

const emptyForm = {
  genericId: '',
  type: '',
  category: '',
  genericName: '',
  status: 'Active'
};

const getGenericLabel = (type) => (
  String(type || '').trim().toLowerCase() === 'drug & medicine' ? 'Generic Name' : 'Item Name'
);

const GenericMaster = () => {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState(emptyForm);
  const [showForm, setShowForm] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [genRes, typeRes, catRes] = await Promise.all([
        api.get('/master/generic/all'),
        api.get('/master/type/all'),
        api.get('/master/medicine-category/all')
      ]);
      if (genRes.data.success) setItems(genRes.data.data || []);
      if (typeRes.data.success) setTypes(typeRes.data.data || []);
      if (catRes.data.success) setAllCategories(catRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextId = async () => {
    try {
      const res = await api.get('/master/generic/next-id');
      if (res.data?.success) {
        setFormData((prev) => ({ ...prev, genericId: res.data.nextId }));
      }
    } catch (_) {}
  };

  useEffect(() => {
    fetchData();
  }, []);

// Filter categories based on selected Type in Form
  const filteredCategories = useMemo(() => {
    if (!formData.type) return [];
    return allCategories.filter(cat => 
      cat.type === formData.type && 
      (cat.status === 'Active' || cat.category_name === formData.category)
    );
  }, [formData.type, allCategories, formData.category]);

  const filteredData = useMemo(() => {
    const sortedList = [...items].sort((a, b) => {
      const idA = String(a.genericId || a._id || '');
      const idB = String(b.genericId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const genericId = (item.genericId || '').toLowerCase();
      const genericName = (item.genericName || '').toLowerCase();
      const type = (item.type || '').toLowerCase();
      const category = (item.category || '').toLowerCase();

      return (
        genericId.startsWith(q) ||
        genericName.startsWith(q) ||
        type.startsWith(q) ||
        category.startsWith(q)
      );
    });
  }, [items, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({ ...item });
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Reset category if type changes
    if (name === 'type') {
        setFormData(prev => ({ ...prev, [name]: value, category: '' }));
    } else if (name === 'status') {
        setFormData(prev => ({ ...prev, [name]: value }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const url = editId ? `/master/generic/save/${editId}` : '/master/generic/save';
      await api.post(url, {
        ...formData,
        type: formData.type.trim().toUpperCase(),
        category: formData.category.trim().toUpperCase(),
        genericName: formData.genericName.trim().toUpperCase()
      });
      toast.success(editId ? 'Updated' : 'Saved');
      setEditId(null);
      setFormData(emptyForm);
      setShowForm(true);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this generic?')) return;
    try {
      await api.delete(`/master/generic/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><Layers size={10} /></div>
          <div>
            <h4 className={masterTitle}>Generic Master</h4>
            <p className={masterSubtitle}>Pharmacological Setup</p>
          </div>
        </div>

        {!showForm ? (
          <button
           onClick={() => { setEditId(null); setFormData(emptyForm); }}
            className={masterAccentButton}
          >
            <Plus size={14} /> Add Generic
          </button>
        ) : (
          <button onClick={() => setShowForm(false)} className={masterPrimaryButton}>
            <ArrowLeft size={14} /> VIEW LIST
          </button>
        )}
      </header>

      <main className="mx-auto max-w-7xl">
        {showForm ? (
          <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-1`}>
            <div className="flex items-center justify-between border-b pb-1">
              <h2 className="text-sm  text-slate-800 font-semibold uppercase">{editId ? 'Edit Generic' : 'New Generic'}</h2>
              <button
                type="button"
                onClick={async () => {
                  setFormData(emptyForm);
                  await fetchNextId();
                }}
                className="text-[10px] font-bold text-rose-500 flex items-center gap-1 uppercase hover:underline"
              >
                <RotateCcw size={12}/> Reset
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-1 gap-y-1">
              {/* <Input label="Generic ID" name="genericId" value={formData.genericId} readOnly /> */}
              
              <Select
                  label="Type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  placeholder="Select Type"
                  options={types
                    .filter(t => t.status === 'Active' || t.typeName === formData.type)
                    .map(t => ({ value: t.typeName, label: t.typeName }))
                  }
                />

              <Select
                label="Category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                placeholder="Select Category"
                disabled={!formData.type}
                options={filteredCategories.map(c => ({ value: c.category_name, label: c.category_name }))}
              />

              <Input label={getGenericLabel(formData.type)} name="genericName" value={formData.genericName} onChange={handleChange} required />
              
              <Select label="Status" name="status" value={formData.status} onChange={handleChange} options={['Active', 'Inactive']} />
            </div>

            <div className="pt-1 border-t flex justify-end">
              <button disabled={isSaving} type="submit" className={`${masterAccentButton} px-4 py-1`}>
                {isSaving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
                {editId ? 'Update' : 'Save'} 
              </button>
            </div>
          </form>
        ) : (
          <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
           <div>
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by generic ID, name, type, or category..." 
                totalItems={filteredData.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>

           
        <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                  <tr>
                    <th className={masterTableData}>S.No</th>
                    <th className={masterTableData}>ID</th>
                    <th className={masterTableData}>Generic / Item Name</th>
                    <th className={masterTableData}>Type</th>
                    <th className={masterTableData}>Category</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                  {loading ? (
                    <tr><td colSpan="7" className="p-12 text-center">Syncing Data...</td></tr>
                  ) : paginatedData.length > 0 ? paginatedData.map((item, index) => (
                    <tr key={item._id} className="hover:bg-emerald-50/30">
                      <td className={masterTableData}>{index + 1 + (currentPage - 1) * pageSize}</td>
                      <td className={masterTableData}>{item.genericId}</td>
                      <td className={masterTableData} title={item.genericName}>
                        {item.genericName}
                      </td>
                      <td className={masterTableData}>{item.type}</td>
                      <td className={masterTableData}>{item.category}</td>
                      <td className={masterTableData}>
                        <span className={`px-2 py-0.5 rounded text-[9px] ${item.status === 'Active' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className={masterTableData}>
                        <div className="flex justify-center gap-1">
                          <button onClick={() => handleEdit(item)} className="p-1 text-emerald-600 rounded-lg hover:bg-emerald-100"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(item._id)} className="p-1 text-rose-600 rounded-lg hover:bg-rose-100"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7" className="p-12 text-center text-slate-300 font-semibold uppercase text-xs">No matching records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, required, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className={masterInputLabel}>{label}</label>
    <input required={required} {...props} className={masterInput} />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className={masterInputLabel}>{label}</label>
    <select {...props} className={masterSelect}>
      {props.placeholder ? <option value="">{props.placeholder}</option> : null}
      {options.map((opt) => {
        const value = typeof opt === 'string' ? opt : opt.value;
        const labelValue = typeof opt === 'string' ? opt : opt.label;
        return <option key={value} value={value}>{labelValue}</option>;
      })}
    </select>
  </div>
);

export default GenericMaster;
